import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Stack,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Typography,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Skeleton,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FitnessCenter as FitnessCenterIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  EventBusy as EventBusyIcon,
} from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { jwtDecode } from "jwt-decode";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, ConfirmDialog, EmptyState, useNotify } from "components/Convokko";

const entrenamientoVacio = {
  _id: null,
  fecha: "",
  hora: "",
  descripcion: "",
  id_equipo: "",
};

const cleanEmptyFields = (obj) => {
  const cleaned = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== "" && String(value).trim() !== "") {
      cleaned[key] = typeof value === "string" ? value.trim() : value;
    }
  });
  return cleaned;
};

const formatDate = (fecha) => {
  if (!fecha) return "Sin fecha";
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return fecha;
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
};

const monthLabel = (fecha) => {
  const d = new Date(fecha);
  if (!fecha || Number.isNaN(d.getTime())) return "Sin fecha";
  const label = d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const isToday = (fecha) => {
  if (!fecha) return false;
  const d = new Date(fecha);
  const hoy = new Date();
  return (
    d.getDate() === hoy.getDate() &&
    d.getMonth() === hoy.getMonth() &&
    d.getFullYear() === hoy.getFullYear()
  );
};

// Teja de fecha para las filas de la agenda: día grande + día de la semana
function DateTile({ fecha }) {
  const d = new Date(fecha);
  const valid = fecha && !Number.isNaN(d.getTime());
  const hoy = valid && isToday(fecha);
  const futuro = valid && !hoy && d > new Date();
  return (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "14px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: hoy ? "rgba(251,181,63,0.18)" : futuro ? "rgba(34,51,107,0.08)" : "grey.100",
        border: "1px solid",
        borderColor: hoy ? "primary.main" : "transparent",
      }}
    >
      <Typography
        variant="h6"
        fontWeight={800}
        lineHeight={1}
        color={hoy ? "#D97706" : futuro ? "#22336B" : "text.primary"}
      >
        {valid ? d.getDate() : "—"}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={700}
        textTransform="uppercase"
        fontSize="0.6rem"
        color={hoy ? "#D97706" : "text.secondary"}
      >
        {hoy ? "HOY" : valid ? d.toLocaleDateString("es-ES", { weekday: "short" }) : "s/f"}
      </Typography>
    </Box>
  );
}

DateTile.propTypes = {
  fecha: PropTypes.string,
};

// Agenda de entrenamientos agrupada por mes, aislada en su propio componente
// memoizado: puede haber cientos de entrenamientos, y no debe reconciliarse
// cada vez que se escribe en el formulario de crear/editar (que vive en el padre).
const AgendaEntrenamientos = React.memo(function AgendaEntrenamientos({
  grupos,
  equiposById,
  onOpenAsistencia,
  onSuspender,
  onEdit,
  onDelete,
}) {
  const getEquipoName = (equipoId) => equiposById[equipoId]?.nombre || "Equipo desconocido";

  return (
    <Stack spacing={3}>
      {grupos.map((grupo) => (
        <Box key={grupo.label}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.25} px={0.5}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              textTransform="uppercase"
              letterSpacing="0.05em"
              color="text.secondary"
              fontSize="0.72rem"
            >
              {grupo.label}
            </Typography>
            <Chip
              label={grupo.items.length}
              size="small"
              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
            />
            <Box flexGrow={1} height="1px" bgcolor="grey.200" />
          </Stack>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
            }}
          >
            {grupo.items.map((ent, idx) => {
              const asistentes = ent.asistencia?.jugadores?.length || 0;
              const pendiente = ent.origen === "automatico" && ent.estado === "pendiente";
              const suspendido = ent.estado === "suspendido";
              return (
                <Box key={ent._id}>
                  {idx > 0 && <Divider />}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={{ xs: 1.5, sm: 2 }}
                    px={{ xs: 1.5, sm: 2.5 }}
                    py={1.25}
                    sx={{
                      transition: "background .15s ease",
                      bgcolor: pendiente ? "rgba(251,181,63,0.08)" : undefined,
                      "&:hover": { bgcolor: pendiente ? "rgba(251,181,63,0.14)" : "grey.100" },
                    }}
                  >
                    <DateTile fecha={ent.fecha} />

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {getEquipoName(ent.id_equipo)}
                        </Typography>
                        {pendiente && (
                          <Chip
                            label="Sin registrar"
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700 }}
                          />
                        )}
                        {suspendido && (
                          <Chip
                            label="Suspendido"
                            size="small"
                            sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {ent.hora && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 13 }} /> {ent.hora}
                          </Typography>
                        )}
                        {ent.descripcion && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {ent.descripcion}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {!suspendido && (
                      <Chip
                        icon={<GroupIcon sx={{ fontSize: "14px !important" }} />}
                        label={asistentes}
                        size="small"
                        color={asistentes > 0 ? "success" : "default"}
                        variant={asistentes > 0 ? "filled" : "outlined"}
                        onClick={() => onOpenAsistencia(ent)}
                        sx={{
                          fontWeight: 700,
                          display: { xs: "none", sm: "inline-flex" },
                          cursor: "pointer",
                        }}
                      />
                    )}

                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <Tooltip title="Pasar asistencia">
                        <IconButton
                          size="small"
                          onClick={() => onOpenAsistencia(ent)}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "success.main" },
                          }}
                        >
                          <GroupIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {pendiente && (
                        <Tooltip title="Marcar como suspendido (no hubo entreno)">
                          <IconButton
                            size="small"
                            onClick={() => onSuspender(ent)}
                            sx={{ color: "text.secondary", "&:hover": { color: "warning.main" } }}
                          >
                            <EventBusyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(ent)}
                          sx={{ color: "text.secondary", "&:hover": { color: "info.main" } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(ent)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Card>
        </Box>
      ))}
    </Stack>
  );
});

AgendaEntrenamientos.propTypes = {
  grupos: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      items: PropTypes.array,
    })
  ).isRequired,
  equiposById: PropTypes.object.isRequired,
  onOpenAsistencia: PropTypes.func.isRequired,
  onSuspender: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function EntrenamientosList() {
  const notify = useNotify();
  const { temporada } = useSeason();

  const [entrenamientos, setEntrenamientos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("entrenador");

  const [searchTerm, setSearchTerm] = useState("");
  const [equipoFiltro, setEquipoFiltro] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEntrenamiento, setCurrentEntrenamiento] = useState(entrenamientoVacio);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Asistencia
  const [openAsistencia, setOpenAsistencia] = useState(false);
  const [asistenciaEntrenamiento, setAsistenciaEntrenamiento] = useState(null);
  const [asistencia, setAsistencia] = useState({ jugadores: [], entrenadores: [] });
  const [loadingAsistencia, setLoadingAsistencia] = useState(false);
  const [selectedJugador, setSelectedJugador] = useState(null);

  useEffect(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token"));
      setUserRole(decoded.rol || "entrenador");
    } catch (e) {
      setUserRole("entrenador");
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [dataEntrenamientos, dataEquipos, dataJugadores] = await Promise.all([
        apiFetch("/api/entrenamientos/ver-entrenamientos"),
        apiFetch("/api/equipos"),
        apiFetch("/api/jugadores/ver-jugadores-equipo"),
      ]);
      setEntrenamientos(Array.isArray(dataEntrenamientos) ? dataEntrenamientos : []);
      setEquipos(Array.isArray(dataEquipos) ? dataEquipos : []);
      setJugadores(Array.isArray(dataJugadores) ? dataJugadores : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const equiposById = useMemo(() => {
    const map = {};
    equipos.forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [equipos]);

  const equiposTemporada = useMemo(
    () => equipos.filter((e) => Number(e.temporada) === Number(temporada)),
    [equipos, temporada]
  );

  const getEquipoName = (equipoId) => equiposById[equipoId]?.nombre || "Equipo desconocido";

  // Coherencia con temporada global: el entrenamiento pertenece a la temporada de su equipo
  const filteredEntrenamientos = useMemo(
    () =>
      entrenamientos.filter((ent) => {
        const equipo = equiposById[ent.id_equipo];
        if (equipo && Number(equipo.temporada) !== Number(temporada)) return false;
        if (equipoFiltro && ent.id_equipo !== equipoFiltro) return false;
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const target = `${ent.descripcion || ""} ${getEquipoName(ent.id_equipo)} ${
            ent.fecha || ""
          }`.toLowerCase();
          if (!target.includes(q)) return false;
        }
        return true;
      }),
    [entrenamientos, equiposById, temporada, equipoFiltro, searchTerm]
  );

  // Agenda: ordenados del más reciente al más antiguo y agrupados por mes
  const gruposPorMes = useMemo(() => {
    const ordenados = [...filteredEntrenamientos].sort((a, b) => {
      const da = new Date(`${a.fecha || "1970-01-01"}T${a.hora || "00:00"}`);
      const db = new Date(`${b.fecha || "1970-01-01"}T${b.hora || "00:00"}`);
      return db - da;
    });
    const grupos = [];
    ordenados.forEach((ent) => {
      const label = monthLabel(ent.fecha);
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.label === label) {
        ultimo.items.push(ent);
      } else {
        grupos.push({ label, items: [ent] });
      }
    });
    return grupos;
  }, [filteredEntrenamientos]);

  // --- CRUD ---
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentEntrenamiento(entrenamientoVacio);
    setOpenModal(true);
  };

  // useCallback: se pasa a AgendaEntrenamientos (memoizado), necesita
  // identidad estable para que el memo evite reconciliar la lista al escribir
  // en el formulario.
  const handleOpenEdit = useCallback((ent) => {
    setEditMode(true);
    setCurrentEntrenamiento({ ...entrenamientoVacio, ...ent });
    setOpenModal(true);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentEntrenamiento((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!currentEntrenamiento.fecha) {
      notify.error("La fecha es obligatoria");
      return;
    }
    try {
      setSaving(true);
      const payload = cleanEmptyFields({
        fecha: currentEntrenamiento.fecha,
        hora: currentEntrenamiento.hora,
        descripcion: currentEntrenamiento.descripcion,
        ...(editMode && currentEntrenamiento._id && { _id: currentEntrenamiento._id }),
        ...(currentEntrenamiento.id_equipo && { id_equipo: currentEntrenamiento.id_equipo }),
      });
      if (editMode) {
        await apiFetch("/api/entrenamientos/actualizar-entrenamiento-equipo", {
          method: "PUT",
          body: payload,
        });
        notify.success("Entrenamiento actualizado");
      } else {
        await apiFetch("/api/entrenamientos/crear-entrenamiento-equipo", {
          method: "POST",
          body: payload,
        });
        notify.success("Entrenamiento creado");
      }
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/entrenamientos/eliminar-entrenamiento-equipo", {
        method: "DELETE",
        params: { entrenamiento_id: deleteTarget._id },
      });
      setEntrenamientos((prev) => prev.filter((e) => e._id !== deleteTarget._id));
      notify.success("Entrenamiento eliminado");
      setDeleteTarget(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- Asistencia ---
  const cargarAsistencia = useCallback(
    async (entrenamientoId) => {
      setLoadingAsistencia(true);
      try {
        const data = await apiFetch("/api/entrenamientos/ver-asistencia-entrenamiento", {
          params: { entrenamiento_id: entrenamientoId },
        });
        setAsistencia({
          jugadores: Array.isArray(data?.jugadores) ? data.jugadores : [],
          entrenadores: Array.isArray(data?.entrenadores) ? data.entrenadores : [],
        });
      } catch (err) {
        notify.error(err.message);
        setAsistencia({ jugadores: [], entrenadores: [] });
      } finally {
        setLoadingAsistencia(false);
      }
    },
    [notify]
  );

  // useCallback: se pasa a AgendaEntrenamientos (memoizado).
  const handleOpenAsistencia = useCallback(
    async (ent) => {
      setAsistenciaEntrenamiento(ent);
      setSelectedJugador(null);
      setOpenAsistencia(true);
      await cargarAsistencia(ent._id);
    },
    [cargarAsistencia]
  );

  const jugadoresDisponibles = useMemo(() => {
    if (!asistenciaEntrenamiento) return [];
    return jugadores.filter((j) => {
      if (j.equipo_actual !== asistenciaEntrenamiento.id_equipo) return false;
      return !asistencia.jugadores.some((a) => (a._id || a.id) === j._id);
    });
  }, [jugadores, asistenciaEntrenamiento, asistencia.jugadores]);

  const addJugador = async (jugador) => {
    if (!jugador || !asistenciaEntrenamiento) return;
    try {
      await apiFetch("/api/entrenamientos/actualizar-jugador-asistencia-equipo", {
        method: "POST",
        params: { jugador_id: jugador._id, entrenamiento_id: asistenciaEntrenamiento._id },
      });
      setSelectedJugador(null);
      await cargarAsistencia(asistenciaEntrenamiento._id);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const addTodos = async () => {
    if (!asistenciaEntrenamiento || jugadoresDisponibles.length === 0) return;
    setLoadingAsistencia(true);
    try {
      for (const jugador of jugadoresDisponibles) {
        // eslint-disable-next-line no-await-in-loop
        await apiFetch("/api/entrenamientos/actualizar-jugador-asistencia-equipo", {
          method: "POST",
          params: { jugador_id: jugador._id, entrenamiento_id: asistenciaEntrenamiento._id },
        }).catch(() => {});
      }
      notify.success("Asistencia completada");
    } finally {
      await cargarAsistencia(asistenciaEntrenamiento._id);
    }
  };

  const removeJugador = async (jugador) => {
    const jugadorId = jugador._id || jugador.id;
    if (!jugadorId || !asistenciaEntrenamiento) return;
    try {
      await apiFetch("/api/entrenamientos/eliminar-jugador-asistencia-equipo", {
        method: "DELETE",
        params: { jugador_id: jugadorId, entrenamiento_id: asistenciaEntrenamiento._id },
      });
      await cargarAsistencia(asistenciaEntrenamiento._id);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const handleCloseAsistencia = async () => {
    if (asistenciaEntrenamiento?.estado === "pendiente") {
      try {
        await apiFetch("/api/entrenamientos/actualizar-entrenamiento-equipo", {
          method: "PUT",
          body: { _id: asistenciaEntrenamiento._id, estado: "registrado" },
        });
      } catch (err) {
        notify.error(err.message);
      }
    }
    setOpenAsistencia(false);
    setAsistenciaEntrenamiento(null);
    await fetchData();
  };

  const handleSuspender = async (ent) => {
    try {
      await apiFetch("/api/entrenamientos/actualizar-entrenamiento-equipo", {
        method: "PUT",
        body: { _id: ent._id, estado: "suspendido" },
      });
      notify.success("Entrenamiento marcado como suspendido");
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    }
  };

  const renderSkeleton = () => (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "grey.200" }}>
      {[...Array(7)].map((_, i) => (
        <Stack key={i} direction="row" spacing={2} alignItems="center" px={2.5} py={1.5}>
          <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: "14px" }} />
          <Box flex={1}>
            <Skeleton variant="text" width="40%" height={22} />
            <Skeleton variant="text" width="25%" height={16} />
          </Box>
          <Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: 3 }} />
        </Stack>
      ))}
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={FitnessCenterIcon}
          title="Entrenamientos"
          count={filteredEntrenamientos.length}
          countLabel={filteredEntrenamientos.length === 1 ? "entrenamiento" : "entrenamientos"}
          action={{
            label: "Nuevo entrenamiento",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar por descripción, equipo o fecha...",
          }}
          filters={[
            {
              id: "equipo",
              label: "Equipo",
              allLabel: "Todos los equipos",
              value: equipoFiltro,
              onChange: setEquipoFiltro,
              options: equiposTemporada.map((e) => ({ value: e._id || e.id, label: e.nombre })),
              minWidth: 130,
            },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : filteredEntrenamientos.length === 0 ? (
          <EmptyState
            icon={FitnessCenterIcon}
            title="No hay entrenamientos"
            subtitle={
              entrenamientos.length === 0
                ? "Aún no hay entrenamientos registrados."
                : "Ningún entrenamiento coincide con los filtros o la temporada seleccionada."
            }
            actionLabel="Crear entrenamiento"
            onAction={handleOpenCreate}
          />
        ) : (
          <AgendaEntrenamientos
            grupos={gruposPorMes}
            equiposById={equiposById}
            onOpenAsistencia={handleOpenAsistencia}
            onSuspender={handleSuspender}
            onEdit={handleOpenEdit}
            onDelete={setDeleteTarget}
          />
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="Crear entrenamiento"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Crear / editar entrenamiento */}
      <ModalShell
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={editMode ? "Editar entrenamiento" : "Nuevo entrenamiento"}
        subtitle={editMode ? getEquipoName(currentEntrenamiento.id_equipo) : ""}
        icon={FitnessCenterIcon}
        actions={
          <>
            <Button onClick={() => setOpenModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={
                saving ||
                !currentEntrenamiento.fecha ||
                (!editMode &&
                  (userRole === "coordinador" || equiposTemporada.length > 1) &&
                  !currentEntrenamiento.id_equipo)
              }
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          {(userRole === "coordinador" || equiposTemporada.length > 1) && (
            <FormControl fullWidth required={!editMode}>
              <InputLabel>Equipo</InputLabel>
              <Select
                name="id_equipo"
                value={currentEntrenamiento.id_equipo || ""}
                label="Equipo"
                onChange={handleChange}
                sx={{ height: 44 }}
              >
                {equiposTemporada.map((eq) => (
                  <MenuItem key={eq._id} value={eq._id}>
                    {eq.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Fecha"
                name="fecha"
                type="date"
                value={currentEntrenamiento.fecha || ""}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora"
                name="hora"
                type="time"
                value={currentEntrenamiento.hora || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <TextField
            label="Descripción"
            name="descripcion"
            value={currentEntrenamiento.descripcion || ""}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            placeholder="Contenido de la sesión, objetivos..."
          />
        </Stack>
      </ModalShell>

      {/* Asistencia */}
      <ModalShell
        open={openAsistencia}
        onClose={handleCloseAsistencia}
        title="Asistencia"
        subtitle={
          asistenciaEntrenamiento
            ? `${getEquipoName(asistenciaEntrenamiento.id_equipo)} · ${formatDate(
                asistenciaEntrenamiento.fecha
              )}`
            : ""
        }
        icon={GroupIcon}
        maxWidth="md"
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Asistieron ({asistencia.jugadores.length}/
              {asistencia.jugadores.length + jugadoresDisponibles.length})
            </Typography>
            {loadingAsistencia ? (
              <Stack spacing={1}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : asistencia.jugadores.length ? (
              <List dense disablePadding sx={{ maxHeight: 360, overflowY: "auto" }}>
                {asistencia.jugadores.map((j) => (
                  <ListItem
                    key={j._id || j.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.200",
                      borderRadius: 2.5,
                      mb: 0.75,
                      pr: 6,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: "success.main" }}
                      >
                        {(j.nombre || "?")[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={j.nombre}
                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Quitar asistencia">
                        <IconButton
                          size="small"
                          onClick={() => removeJugador(j)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                Nadie registrado todavía.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Añadir jugadores
            </Typography>
            <Stack spacing={1.5}>
              <Autocomplete
                value={selectedJugador}
                onChange={(e, v) => v && addJugador(v)}
                options={jugadoresDisponibles}
                getOptionLabel={(o) => o.nombre || ""}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar jugador" size="small" />
                )}
                noOptionsText="No quedan jugadores disponibles"
              />
              <Button
                variant="outlined"
                color="success"
                startIcon={<GroupAddIcon />}
                onClick={addTodos}
                disabled={jugadoresDisponibles.length === 0 || loadingAsistencia}
                sx={{ borderRadius: 2.5 }}
              >
                Marcar todos ({jugadoresDisponibles.length})
              </Button>
              <List dense disablePadding sx={{ maxHeight: 280, overflowY: "auto" }}>
                {jugadoresDisponibles.map((j) => (
                  <ListItem
                    key={j._id}
                    button
                    onClick={() => addJugador(j)}
                    sx={{
                      borderRadius: 2.5,
                      mb: 0.5,
                      "&:hover": { bgcolor: "rgba(76,175,80,.08)" },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{ width: 30, height: 30, fontSize: "0.75rem", bgcolor: "grey.400" }}
                      >
                        {(j.nombre || "?")[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={j.nombre}
                      secondary={j.posicion}
                      primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: "0.72rem" }}
                    />
                    <PersonAddIcon sx={{ fontSize: 18, color: "success.main" }} />
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Grid>
        </Grid>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar entrenamiento"
        message={
          <>
            ¿Seguro que quieres eliminar el entrenamiento de{" "}
            <b>{deleteTarget ? getEquipoName(deleteTarget.id_equipo) : ""}</b> del{" "}
            {formatDate(deleteTarget?.fecha)}? Se perderá también su registro de asistencia.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
